import { FunctionComponent, RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { TextMarkdown } from "../../../components/Shared/Components";
import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { confirmActionPopup } from "../../../components/Shared/Functions/ConfirmActionPopupFunction";
import { translationKeys } from "../../../models/app-translation-keys";
import { useDisclaimerService } from "../../../services/ServiceHooks/disclaimerHook";
import { useStore } from "../../../stores/StoreContext";
import CheckboxDoNotShowAgain, { CheckboxHandle } from "./PopupDoNotShowAgain/CheckboxDoNotShowAgain";

import "./ImageGenDisclaimer.scss";

function getWhitelabelBrandColor(): string {
  return getComputedStyle(document.documentElement).getPropertyValue("--customer-brand-color-background")?.trim();
}

const spinnerDefaultColor = getWhitelabelBrandColor();
const imageGenTranslationKeys = translationKeys.feature.imageGeneration;

interface IImageGenDisclaimerProps {
  doNotShowAgainKey?: string;
  onApprove?: () => void;
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
}

export const ImageGenDisclaimer: FunctionComponent<IImageGenDisclaimerProps> = observer(
  ({ doNotShowAgainKey, onApprove, isActive, setIsActive }) => {
    const { t } = useTranslation();
    const { userStore } = useStore();
    const disclaimerService = useDisclaimerService();

    const toggleImageGenButtonRef = useRef<HTMLDivElement>(null);
    const checkboxIdRef = useRef(`imagegenDontShowAgain_${Math.random().toString(36).slice(2)}`);
    const popupCheckboxRef = useRef<CheckboxHandle | null>(null);

    const [text, setText] = useState<string | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const isImageGenActive = isActive;

    const doNotShowAgainStatus = (): boolean =>
      !!((doNotShowAgainKey && sessionStorage.getItem(doNotShowAgainKey)) || "");

    // build the final message node by appending the checkbox if requested
    const finalMessageNode = (
      <div>
        <h2>{t(imageGenTranslationKeys.disclaimer.title)}</h2>

        <TextMarkdown>{text ?? ""}</TextMarkdown>

        {doNotShowAgainKey && (
          <CheckboxDoNotShowAgain
            ref={popupCheckboxRef}
            inputId={checkboxIdRef.current}
            checkedByDefault={doNotShowAgainStatus()}
            label={t(imageGenTranslationKeys.actions.doNotShowAgain)}
          />
        )}
      </div>
    );

    const confirmStartImageGeneration = async (target: RefObject<HTMLDivElement | null>): Promise<boolean> => {
      return confirmActionPopup(t, {
        target: target,
        messageNode: finalMessageNode,
        defaultFocus: "accept",
        rejectLabel: t(translationKeys.actions.cancel),
        acceptIcon: <FontAwesomeIcon icon={faArrowRight} />,
        acceptLabel: t(imageGenTranslationKeys.actions.startImageGeneration),
        acceptClassName: "inline-flex align-items-center gap-2 icon-button-reverse",
        style: { maxWidth: "450px" },
        acceptAction: () => {
          if (doNotShowAgainKey) {
            const checked = popupCheckboxRef.current?.isChecked() ?? false;
            if (checked) {
              sessionStorage.setItem(doNotShowAgainKey, "true");
            } else {
              sessionStorage.removeItem(doNotShowAgainKey);
            }
          }
        },
      });
    };

    const tryToggleImageGeneration = async () => {
      if (isImageGenActive) {
        setIsActive(false);
        return;
      }

      // Same flow as chat Disclaimer: activate immediately when "do not show again"
      // is set; otherwise activate only after popup approval.
      if (doNotShowAgainStatus() || (await confirmStartImageGeneration(toggleImageGenButtonRef))) {
        setIsActive(true);
        if (onApprove) {
          onApprove();
        }
      }
    };

    const loadTranslationByLanguage = async () => {
      setLoading(true);
      try {
        const result = await disclaimerService.getDisclaimerText(userStore.currentLanguage.code, "imagegen");
        setText(result.text);
      } catch (error: unknown) {
        setText(undefined);
        if ((error as { response?: { status?: number } })?.response?.status !== 404) {
          console.error("Failed to load imagegen disclaimer", error);
        }
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      loadTranslationByLanguage();
    }, [userStore.currentLanguage.code]);

    const imageGenButton = () => {
      return (
        <div
          className={classNames("toggle-imagegen-button", {
            disabled: loading,
            active: isImageGenActive,
          })}
          onClick={loading ? undefined : tryToggleImageGeneration}
          onKeyDown={event => event.key === "Enter" && (loading ? undefined : tryToggleImageGeneration())}
          tabIndex={0}
          role="button"
          data-testid="toggleImageGenButton"
          ref={toggleImageGenButtonRef}
        >
          <TooltipWrapper
            targetRef=".toggle-imagegen-button"
            position="top"
            content={t(
              isImageGenActive
                ? imageGenTranslationKeys.tooltips.imageGenerationActive
                : imageGenTranslationKeys.tooltips.imageGenerationInactive,
            )}
          />
          IMG
        </div>
      );
    };

    return loading ? (
      <ProgressSpinner className="small-medium-inline-spinner" color={spinnerDefaultColor} />
    ) : (
      imageGenButton()
    );
  },
);
